import { useTranslator } from '@bluebeam/react-i18n';
import classnames from 'classnames';
import Search from 'components/Icons/Search';
import debounce from 'lodash/debounce';
import React, { CSSProperties, FC, useState } from 'react';
import { GeoLocation, RawGeoLocation } from 'records/GeoLocation';
import searchClear from 'images/icons/search-clear.svg';
import InfoCircle from 'components/Icons/InfoCircle';

interface Props {
  id: string;
  onChange: (searchString: string) => void;
  onSelect: (geoLocation: RawGeoLocation) => void;
  placeholder: string;
  defaultValue?: string;
  disabled?: boolean;
  disableDebounce?: boolean;
  hideSearchIcon?: boolean;
  results?: Array<GeoLocation> | null;
  style?: CSSProperties;
  isLoading?: boolean;
  hasError?: boolean;
  autoSearchResults?: boolean | null;
}

const SearchInputResults: FC<Props> = (props: Props) => {
  const {
    id,
    onChange,
    onSelect,
    placeholder,
    disabled,
    disableDebounce,
    hideSearchIcon,
    results,
    style,
    isLoading,
    hasError,
    defaultValue,
    autoSearchResults = true,
  } = props;

  const [value, setValue] = useState(defaultValue);
  const [showResults, setShowResults] = useState(
    defaultValue !== '' && autoSearchResults
  );
  const { translate } = useTranslator(['geolocationModal']);
  const maxSearchResults = 4;

  const showNoResultsMessage =
    (!results || results.length === 0) && !isLoading && !hasError;

  const onChangeDebounced = debounce((searchString: string): void => {
    onChange(searchString);
  }, 100);

  const handleSearchChange = (
    evt: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const targetValue = evt.target.value;
    if (disableDebounce) {
      onChange(targetValue);
    } else {
      onChangeDebounced(targetValue);
    }
    setValue(targetValue);
    setShowResults(targetValue !== '');
  };

  const handleResultClick = (selectedValue: GeoLocation) => () => {
    setValue(selectedValue.place_name);

    const newSelectedValue: RawGeoLocation = {
      id: selectedValue.id,
      center: selectedValue.center,
      place_name: selectedValue.place_name,
      bbox: selectedValue.bbox,
      matching_place_name: selectedValue.matching_place_name,
      text: selectedValue.text,
    };

    onSelect(newSelectedValue);
    setShowResults(false);
  };

  const handleInputClick = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLInputElement;
    const targetValue = target.value;
    setShowResults(targetValue !== '');
  };

  const className = `${id}-input`;

  return (
    <>
      <div
        id={id}
        className={classnames('search-wrapper hs2', {
          'cursor-not-allowed opacity-50': disabled,
        })}
        style={style}
      >
        <style>
          {`
          #${className}::-webkit-search-cancel-button {
            opacity: 0;
          }

          #${className}:focus-within::-webkit-search-cancel-button {
            -webkit-appearance: none;
            appearance: none;
            height: 24px;
            width: 24px;
            background-image: url(${searchClear});
            background-size: 24px 24px;
            cursor: pointer;
            opacity: 0.5;
            margin-right: -6px;
          }

          #${className}::-webkit-search-cancel-button:focus-within:hover {
            opacity: 1;
          }
          `}
        </style>
        <div className="row items-center">
          <div
            className="icon"
            style={{ display: hideSearchIcon ? 'none' : 'flex' }}
          >
            <Search width="16px" height="16px" style={{ fill: '#7C8385' }} />
          </div>
          <input
            disabled={disabled}
            id={`${id}-input`}
            value={value}
            type="search"
            placeholder={placeholder}
            className={`${className} light`}
            autoComplete="off"
            style={{
              width: '100%',
              height: '100%',
              border: 'transparent',
              borderRadius: '8px',
              outline: 'none',
              paddingLeft: hideSearchIcon ? '16px' : '0px',
              fontSize: '100%',
            }}
            onChange={handleSearchChange}
            onClick={handleInputClick}
          />
        </div>
      </div>
      {showResults && (
        <div className="search-results-wrapper">
          {results && (
            <>
              {isLoading && (
                <div className="list loading">
                  {results
                    .slice(0, maxSearchResults)
                    .map((item: GeoLocation) => (
                      <div className="list-item" key={item.id}>
                        <span className="loading-bar short">&nbsp;</span>
                        <span className="loading-bar long">&nbsp;</span>
                      </div>
                    ))}
                </div>
              )}

              {!isLoading && results.length > 0 && (
                <div className="list">
                  {results
                    .slice(0, maxSearchResults)
                    .map((item: GeoLocation) => {
                      const locationTitle = item.text;
                      const locationSubTitle =
                        item.matching_place_name || item.place_name;

                      return (
                        <div
                          className="list-item link"
                          key={item.id}
                          onClick={handleResultClick(item)}
                        >
                          {locationTitle}
                          <span>{locationSubTitle}</span>
                        </div>
                      );
                    })}
                </div>
              )}
            </>
          )}

          {hasError && (
            <div className="list">
              <div className="list-item error">
                <div className="icon">
                  <InfoCircle />
                </div>
                {translate('geolocationModal:serverError')}
              </div>
            </div>
          )}

          {showNoResultsMessage && (
            <div className="list no-results">
              <div className="list-item">
                {translate('geolocationModal:noResults')}
                <span>{translate('geolocationModal:noMatchingKeywords')}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default SearchInputResults;
